import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { alpha, useTheme, Stack } from '@mui/material';
import { Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { AppBaseButton } from '../../components/AppBaseButton/AppBaseButton';
import { EditDialog } from '../../components/EditDialog/EditDialog';
import { NumericTextField } from '../../components/FormFields/NumericTextField';
import { isRoofDeflectionLimit, DeflectionLimitRoof, DeflectionLimitWall, Standards } from 'types/domainTypes';
import _ from 'lodash';

interface DeflectionLimitsDialogProps {
  data: DeflectionLimitWall | DeflectionLimitRoof;
  standard: Standards;
  onClose: () => void;
  onSave: (data: DeflectionLimitWall | DeflectionLimitRoof) => void;
}

export function HasCustomisedValues(standard: Standards, data: DeflectionLimitWall | DeflectionLimitRoof): boolean {
  if (isRoofDeflectionLimit(data)) {
    return !_.isEqual(data, standard.deflectionLimitRoof);
  } else {
    return !_.isEqual(data, standard.deflectionLimitWall);
  }
}

export function DeflectionLimitsDialog({ data, standard, onClose, onSave }: DeflectionLimitsDialogProps): ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();

  const isRoofDialog = isRoofDeflectionLimit(data);
  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<DeflectionLimitWall | DeflectionLimitRoof>({ reValidateMode: 'onChange', defaultValues: data });

  const resetToStandard = () => {
    const defaults = isRoofDialog ? standard.deflectionLimitRoof : standard.deflectionLimitWall;
    reset(defaults, { keepDefaultValues: true });
  };

  const isCustomised = HasCustomisedValues(standard, watch());
  return (
    <EditDialog
      onClose={onClose}
      handleFormSubmit={handleSubmit(onSave)}
      isDirty={isDirty}
      title={
        isRoofDialog
          ? t('project-details-page.deflection-dialog.edit-deflection-limit-roof')
          : t('project-details-page.deflection-dialog.edit-deflection-limit-wall')
      }
    >
      <Stack spacing={2}>
        <Stack direction={'row'} alignItems="center" justifyContent="space-between">
          <Typography variant="body2" color={theme.palette.common.black}>
            <span style={{ color: alpha(theme.palette.common.black, 0.6) }}>
              {isRoofDialog
                ? t('project-details-page.deflection-dialog.label-deflection-limit-roof')
                : t('project-details-page.deflection-dialog.label-deflection-limit-wall')}
              :&nbsp;
            </span>
            {data.name}
          </Typography>
          <AppBaseButton disabled={!isCustomised} onClick={resetToStandard}>
            {t('project-details-page.deflection-dialog.reset-values')}
          </AppBaseButton>
        </Stack>

        <NumericTextField
          name="pressureLoad"
          control={control}
          fullWidth
          label={t('project-details-page.deflection-dialog.label-pressure-load')}
          required
        />
        <NumericTextField
          name="suctionLoad"
          control={control}
          fullWidth
          label={t('project-details-page.deflection-dialog.label-suction-load')}
          required
        />
        {isRoofDialog && (
          <NumericTextField
            name="pressureLoadWithCreep"
            control={control}
            fullWidth
            label={t('project-details-page.deflection-dialog.label-pressure-creep-load')}
            required
          />
        )}
        <NumericTextField
          name="cantileverLoad"
          control={control}
          fullWidth
          label={t('project-details-page.deflection-dialog.label-cantilevers')}
          required
        />
        {isRoofDialog && (
          <NumericTextField
            name="cantileverLoadWithCreep"
            control={control}
            fullWidth
            label={t('project-details-page.deflection-dialog.label-cantilevers-creep')}
            required
          />
        )}
      </Stack>
    </EditDialog>
  );
}
